import { useCallback, useEffect, useRef, useState } from 'react'
import FileUploadService from '../shared/services/file-upload/file-upload.service'
import { IBillDocument, IBillExtractionProgress } from '../shared/services/file-upload/file-upload.model'
import { isBillReading } from '../shared/utils/bill-upload-utils'

/**
 * The bills of one bill upload, with the batch's reading progress. Polls every 5 seconds (the
 * import wizard's cadence) while any bill is still being read, and stops once all are done.
 * `onReadingFinished` fires once on the reading-to-done transition, so a caller can refetch the rows
 * that only exist from that moment. Pass a null id for a spreadsheet upload: nothing is fetched.
 */
export const useBillDocuments = (fileUploadId: string | null | undefined, onReadingFinished?: () => void) => {
  const fileUploadServiceRef = useRef(new FileUploadService())
  const onReadingFinishedRef = useRef(onReadingFinished)
  onReadingFinishedRef.current = onReadingFinished
  const wasReadingRef = useRef(false)
  const [documents, setDocuments] = useState<IBillDocument[]>([])
  const [progress, setProgress] = useState<IBillExtractionProgress | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(!!fileUploadId)
  const [isReading, setIsReading] = useState<boolean>(false)
  const [refreshCount, setRefreshCount] = useState(0)

  const refresh = useCallback(() => setRefreshCount((count) => count + 1), [])

  useEffect(() => {
    if (!fileUploadId) {
      setDocuments([])
      setIsLoading(false)
      return
    }
    let cancelled = false
    let interval: NodeJS.Timeout | null = null

    const fetchDocuments = async () => {
      try {
        const response = await fileUploadServiceRef.current.getBillDocuments(fileUploadId)
        if (cancelled) return
        const fetched = response.billDocuments || []
        const reading = fetched.some(isBillReading)
        setDocuments(fetched)
        setProgress(response.progress || null)
        setIsReading(reading)
        setIsLoading(false)
        if (wasReadingRef.current && !reading) {
          onReadingFinishedRef.current?.()
        }
        wasReadingRef.current = reading
        if (!reading && interval) {
          clearInterval(interval)
          interval = null
        }
      } catch (error) {
        console.log(error)
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchDocuments()
    interval = setInterval(fetchDocuments, 5000)
    return () => {
      cancelled = true
      if (interval) clearInterval(interval)
    }
  }, [fileUploadId, refreshCount])

  return { documents, progress, isLoading, isReading, refresh }
}
